import * as React from "react";
import { cn } from "../utils";

export interface SelectableRowProps extends React.ComponentProps<"button"> {
  /**
   * Selected state — applies the `bg-selected` fill (for filter/launcher menus
   * that keep a row "active"). Default false.
   */
  active?: boolean;
  /** Vertical alignment of the row's content. Default `"center"`. */
  align?: "start" | "center";
}

/**
 * SelectableRow — a clickable row inside a connected vertical list: filter
 * menus, the Play "choose your own adventure" launcher, and similar
 * single-pick lists.
 *
 * Owns the list-row click affordance so callers never hand-roll
 * `cursor-pointer` / `hover:bg-*` (CLAUDE.md Hard Rule 10): rounded outer
 * corners, a hairline divider between rows (`first/last` aware), the
 * hover + focus-visible treatment, and an optional `active` fill. Compose the
 * row's contents (icon, label, count, chevron) as children.
 */
export const SelectableRow = React.forwardRef<HTMLButtonElement, SelectableRowProps>(
  function SelectableRow(
    { active = false, align = "center", className, type = "button", ...props },
    ref,
  ) {
    return (
      <button
        ref={ref}
        type={type}
        className={cn(
          // RowShell metrics — the crowned px-4 gutter + the shared --row-py
          // rhythm (VERTICAL-RHYTHM-PRD §T3 / D3). This is a `<button>` with its
          // own first/last rounding, focus ring, and active fill, so it adopts
          // the metric rather than the <RowShell> wrapper `<div>`.
          "group flex gap-3 border-b border-border/40 px-[var(--content-gutter)] py-[var(--row-py)] text-left transition-colors",
          "first:rounded-t-md last:rounded-b-md last:border-b-0",
          "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary",
          align === "start" ? "items-start" : "items-center",
          active ? "bg-selected" : "hover:bg-selected/50",
          className,
        )}
        {...props}
      />
    );
  },
);
